import React, { useState } from 'react';
import { Modal, Table, Tag, Input, Button, Space, Badge, Typography, Tooltip } from 'antd';
import {
  SearchOutlined,
  ExportOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  SplitCellsOutlined,
  RollbackOutlined,
  FileSearchOutlined
} from '@ant-design/icons';

const { Text } = Typography;

// Helper to format Firestore timestamp or Date into YYYY/MM/DD HH:mm string
const formatTimestamp = (val) => {
  if (!val) return '';
  let dateObj = null;
  if (typeof val.toDate === 'function') {
    dateObj = val.toDate();
  } else if (val && val._seconds !== undefined) {
    dateObj = new Date(val._seconds * 1000 + (val._nanoseconds || 0) / 1000000);
  } else if (val instanceof Date) {
    dateObj = val;
  } else if (typeof val === 'string' || typeof val === 'number') {
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) dateObj = parsed;
  }

  if (!dateObj) return String(val);

  const n = (num) => (num > 9 ? `${num}` : `0${num}`);
  const yyyy = dateObj.getFullYear();
  const mm = n(dateObj.getMonth() + 1);
  const dd = n(dateObj.getDate());
  const hh = n(dateObj.getHours());
  const mi = n(dateObj.getMinutes());
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
};

// Helper to format Date into YYYY/MM/DD string
const formatDateOnly = (val) => {
  if (!val) return '----/--/--';
  let dateObj = null;
  if (typeof val.toDate === 'function') {
    dateObj = val.toDate();
  } else if (val && val._seconds !== undefined) {
    dateObj = new Date(val._seconds * 1000 + (val._nanoseconds || 0) / 1000000);
  } else if (val instanceof Date) {
    dateObj = val;
  } else if (typeof val === 'string' || typeof val === 'number') {
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) dateObj = parsed;
  }

  if (!dateObj) return String(val);

  const n = (num) => (num > 9 ? `${num}` : `0${num}`);
  const yyyy = dateObj.getFullYear();
  const mm = n(dateObj.getMonth() + 1);
  const dd = n(dateObj.getDate());
  return `${yyyy}/${mm}/${dd}`;
};

// Helper for SDS preferred months
const getPreferredMonths = (preferredDate) => {
  if (!preferredDate) return '通年';
  const months = [];
  for (let i = 1; i <= 12; i++) {
    if (preferredDate[i]) {
      months.push(`${i}月`);
    }
  }
  return months.length > 0 ? months.join(', ') : '通年';
};

// Calculate Date Label matching OrderTableList.js
const getDateLabel = (statusKey) => {
  switch (statusKey) {
    case 'notShipped':
    case 'shipped':
    case 'cancelPending':
    case 'returnPending':
    case 'splitPending':
      return '更新日時';
    case 'unread':
      return '受注日時';
    default:
      return '発注日時';
  }
};

// Get the actual Date object or string for sorting/formatting matching statusKey date field
const getFormattedDate = (record, statusKey) => {
  let dateVal = null;
  if (statusKey === 'unread') {
    dateVal = record.sortDate || record.latestUpdate || record.created || record.orderDate;
  } else if (
    statusKey === 'notShipped' ||
    statusKey === 'shipped' ||
    statusKey === 'cancelPending' ||
    statusKey === 'returnPending' ||
    statusKey === 'splitPending'
  ) {
    dateVal = record.latestUpdate || record.sortDate || record.created || record.orderDate;
  } else {
    dateVal = record.sortDate || record.latestUpdate || record.created || record.orderDate;
  }
  return formatTimestamp(dateVal) || record.orderDate || '2026/09/08 10:30';
};

// Get numeric timestamp for accurate sorting
const getRawDateTimestamp = (record, statusKey) => {
  let val = record.sortDate;
  if (statusKey !== 'unread') {
    val = record.latestUpdate || record.sortDate || record.created;
  }
  if (!val) return 0;
  if (typeof val.toDate === 'function') return val.toDate().getTime();
  if (val && val._seconds !== undefined) return val._seconds * 1000;
  if (val instanceof Date) return val.getTime();
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

// Calculate order title and count matching OrderTableList.js logic
const getOrderItemsInfo = (record) => {
  const items = record.cart?.items || (Array.isArray(record.items) ? record.items : (record.items ? [record.items] : []));
  if (!items || items.length === 0) {
    const title = record.title || '注文商品';
    return { title, count: 1 };
  }

  const title = items[0]?.name || record.title || '注文商品';
  let count = 0;
  items.forEach((item) => {
    if (!item.divided) {
      count += (item.returnSub === true ? 0 : (item.count || item.quantity || 1));
    }
  });
  if (count === 0) count = items.length || 1;
  return { title, count };
};

// Calculate subtotal amount matching OrderTableList.js total variable logic
const calculateOrderSubtotal = (record) => {
  const items = record.cart?.items || (Array.isArray(record.items) ? record.items : []);
  let total = 0;
  let hasValidItemTotal = false;

  if (items && items.length > 0) {
    items.forEach((item) => {
      if (!item.deletedItem && !item.divided) {
        const price = item.price !== undefined ? item.price : (item.unitPrice || 0);
        const qty = item.count !== undefined ? item.count : (item.quantity || 1);
        if (price > 0 || qty > 0) {
          total += (item.returnSub === true ? 0 : qty * price);
          hasValidItemTotal = true;
        }
      }
    });
  }

  if (!hasValidItemTotal || total === 0) {
    total = record.cart?.total || record.totalAmount || 0;
  }
  return total;
};

const OrderListModal = ({
  visible,
  onClose,
  statusKey,
  statusLabel,
  categoryLabel,
  orders = [],
  onSelectOrder
}) => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isSdsCategory =
    categoryLabel === 'SDS管理' ||
    (statusKey && statusKey.startsWith('sds'));

  const filteredData = orders.filter((order) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    const { title } = getOrderItemsInfo(order);
    const sdsProductName = order.item?.P4CLOUD商品名 || order.item?.name || '';
    return (
      (order.orderNo || '').toLowerCase().includes(term) ||
      (order.id || '').toLowerCase().includes(term) ||
      (order.partnerName || order.buyerName || order.sellerName || '').toLowerCase().includes(term) ||
      (order.notes || '').toLowerCase().includes(term) ||
      title.toLowerCase().includes(term) ||
      sdsProductName.toLowerCase().includes(term)
    );
  });

  const renderStatusTag = (text, record) => {
    const key = record.statusKey || statusKey;
    switch (key) {
      case 'unread':
        return <Tag color="blue" icon={<ClockCircleOutlined />}>未読（未処理）</Tag>;
      case 'notShipped':
        return <Tag color="gold" icon={<SyncOutlined spin />}>未出荷</Tag>;
      case 'shipped':
        return <Tag color="green" icon={<CheckCircleOutlined />}>出荷済</Tag>;
      case 'cancelPending':
        return <Tag color="volcano" icon={<ExclamationCircleOutlined />}>キャンセル承認待</Tag>;
      case 'returnPending':
        return <Tag color="magenta" icon={<RollbackOutlined />}>返品承認待</Tag>;
      case 'splitPending':
        return <Tag color="purple" icon={<SplitCellsOutlined />}>分納承認待</Tag>;
      case 'sdsRequest':
        return <Tag color="cyan" icon={<ClockCircleOutlined />}>SDS申込依頼</Tag>;
      case 'sdsNormal':
        return <Tag color="geekblue" icon={<CheckCircleOutlined />}>SDS正常更新中</Tag>;
      case 'sdsExpired':
        return <Tag color="red" icon={<CloseCircleOutlined />}>SDSチェック更新期限切れ</Tag>;
      default:
        return <Tag color="blue">{text || statusLabel}</Tag>;
    }
  };

  // Render SDS Status matching SDSManagementTableList.js
  const renderSdsStatusBadge = (record) => {
    const status = record.status || (record.statusKey === 'sdsNormal' ? 'normal' : record.statusKey === 'sdsExpired' ? 'alert' : 'waiting');
    switch (status) {
      case 'normal':
        return <Tag color="blue" icon={<CheckCircleOutlined />}>正常更新中</Tag>;
      case 'warning':
        return <Tag color="gold" icon={<ExclamationCircleOutlined />}>期限間近</Tag>;
      case 'alert':
      case 'expired':
        return <Tag color="red" icon={<CloseCircleOutlined />}>チェック更新期限切れ</Tag>;
      case 'canceled':
        return <Tag color="magenta">キャンセル済</Tag>;
      case 'waiting':
      default:
        return <Tag color="cyan" icon={<ClockCircleOutlined />}>申込依頼</Tag>;
    }
  };

  // Order Columns (Cards 1 to 6 - matching OrderTableList.js mdbTable layout)
  const orderColumns = [
    {
      title: '発注番号 / 発注書ID',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 170,
      render: (text, record) => {
        const orderNo = text || record.cart?.orderNo || record.id;
        const isUnread = record.statusKey === 'unread' || record.isUnread;
        return (
          <div>
            <div style={{ fontWeight: isUnread ? 'bold' : '600', color: '#1677ff' }}>
              {orderNo}
            </div>
            {record.id && record.id !== orderNo && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                ID: {record.id}
              </Text>
            )}
            <Space size={4} style={{ marginTop: 4 }} wrap>
              {record.subDivided && (
                <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>分納手続中</Tag>
              )}
              {record.cancelWait && (
                <Tag color="volcano" style={{ fontSize: 10, margin: 0 }}>キャンセル申請中</Tag>
              )}
              {record.returnWait && (
                <Tag color="magenta" style={{ fontSize: 10, margin: 0 }}>返品申請中</Tag>
              )}
            </Space>
          </div>
        );
      }
    },
    {
      title: getDateLabel(statusKey),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 140,
      defaultSortOrder: 'descend',
      sorter: (a, b) => getRawDateTimestamp(a, statusKey) - getRawDateTimestamp(b, statusKey),
      render: (_, record) => (
        <Text style={{ fontSize: 13 }}>
          {getFormattedDate(record, statusKey)}
        </Text>
      )
    },
    {
      title: '発注書受取者 / 受注確認者',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: 210,
      render: (text, record) => {
        const partner = text || record.sellerName || record.buyerName || 'TESTペイント株式会社';
        const isConfirmed = record.sellerConfirm !== false;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{partner}</div>
            <div style={{ marginTop: 2 }}>
              {isConfirmed ? (
                <Tag color="cyan" style={{ fontSize: 11 }}>受注確認済</Tag>
              ) : (
                <Tag color="red" style={{ fontSize: 11 }}>受注未確認</Tag>
              )}
              {record.contactPerson && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                  {record.contactPerson}
                </Text>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: '発注内容',
      dataIndex: 'items',
      key: 'items',
      render: (_, record) => {
        const { title, count } = getOrderItemsInfo(record);
        return (
          <div>
            <Text strong style={{ display: 'block' }}>{title}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              計 <strong style={{ color: '#1677ff', fontSize: 13 }}>{count.toLocaleString()}</strong> 点
            </Text>
          </div>
        );
      }
    },
    {
      title: '小計金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      width: 130,
      sorter: (a, b) => calculateOrderSubtotal(a) - calculateOrderSubtotal(b),
      render: (_, record) => {
        const total = calculateOrderSubtotal(record);
        return (
          <Text strong style={{ color: '#262626', fontSize: 14 }}>
            {total.toLocaleString()}円
          </Text>
        );
      }
    },
    {
      title: '受注 / 状態',
      dataIndex: 'statusText',
      key: 'statusText',
      width: 145,
      render: (text, record) => renderStatusTag(text, record)
    },
    {
      title: '希望納期',
      dataIndex: 'orderNouki',
      key: 'orderNouki',
      width: 110,
      render: (text, record) => record.deliveryDate || record.orderNouki || '最短'
    },
    {
      title: '詳細',
      key: 'action',
      align: 'center',
      width: 85,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="新しいタブで詳細を開く">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<ExportOutlined />}
            onClick={() => {
              const orderId = record.id || record.orderNo;
              window.open(`/order/${orderId}`, '_blank');
            }}
          >
            表示
          </Button>
        </Tooltip>
      )
    }
  ];

  // SDS Columns (Cards 7 to 9 - 100% matching SDSManagementTableList.js mdbTable layout)
  const sdsColumns = [
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (_, record) => renderSdsStatusBadge(record)
    },
    {
      title: '商品名',
      dataIndex: 'item',
      key: 'item',
      render: (item, record) => {
        const productName = item?.P4CLOUD商品名 || item?.name || record.partnerName || 'SDS化学物質情報データ';
        return (
          <div>
            <Text strong style={{ color: '#096dd9', fontSize: 14, display: 'block' }}>
              {productName}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              管理ID: {record.orderNo || record.id}
            </Text>
          </div>
        );
      }
    },
    {
      title: 'SDS / RA状況',
      dataIndex: 'riskManagementStatus',
      key: 'riskManagementStatus',
      width: 160,
      render: (rmStatus, record) => {
        const hasSdsPdf = record.item?.files?.some(f => f?.name && (f.name.toLowerCase().includes('sds') || f.name.toLowerCase().includes('ｓｄｓ')));
        return (
          <Space direction="vertical" size={2}>
            {hasSdsPdf ? (
              <Tag color="cyan" style={{ fontSize: 11 }}>SDS PDFあり</Tag>
            ) : (
              <Tag color="blue" style={{ fontSize: 11 }}>SDS文書データ</Tag>
            )}
            {rmStatus === 2 ? (
              <Tag color="geekblue" style={{ fontSize: 10 }}>RA管理中</Tag>
            ) : rmStatus === 1 ? (
              <Tag color="orange" style={{ fontSize: 10 }}>RA受付待</Tag>
            ) : (
              <Tag color="default" style={{ fontSize: 10 }}>RA未申込</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: <>申込日/申込者<br />受付日/受付者</>,
      dataIndex: 'created',
      key: 'datesUsers',
      width: 220,
      render: (_, record) => {
        const appDate = formatDateOnly(record.created);
        const appUser = record.applicationUserName || '---';
        const recDate = formatDateOnly(record.receptionDate);
        const recUser = record.receptionUserName || '---';
        return (
          <div style={{ fontSize: 12, lineHeight: 1.4 }}>
            <div><Text type="secondary">申込:</Text> {appDate} ({appUser})</div>
            <div><Text type="secondary">受付:</Text> {recDate} ({recUser})</div>
          </div>
        );
      }
    },
    {
      title: 'SDS更新希望月',
      dataIndex: 'preferredDate',
      key: 'preferredDate',
      align: 'center',
      width: 130,
      render: (preferredDate) => (
        <Tag color="purple" style={{ fontSize: 12 }}>
          {getPreferredMonths(preferredDate)}
        </Tag>
      )
    },
    {
      title: 'SDS最終チェック日/実施者',
      dataIndex: 'lastCheckDate',
      key: 'lastCheckDate',
      width: 190,
      render: (lastCheckDate, record) => {
        const formattedCheckDate = formatDateOnly(lastCheckDate);
        const checkUser = record.lastCheckUserName || '----';
        return (
          <div style={{ fontSize: 12 }}>
            <div><strong>{formattedCheckDate}</strong></div>
            <Text type="secondary" style={{ fontSize: 11 }}>{checkUser}</Text>
          </div>
        );
      }
    },
    {
      title: 'SDS改定日',
      dataIndex: 'revisionDate',
      key: 'revisionDate',
      width: 120,
      render: (revisionDate) => (
        <Text style={{ fontSize: 13 }}>
          {formatDateOnly(revisionDate)}
        </Text>
      )
    },
    {
      title: '詳細',
      key: 'action',
      align: 'center',
      width: 85,
      render: () => (
        <Tooltip title="新しいタブでSDS管理画面を開く">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<ExportOutlined />}
            onClick={() => {
              window.open('/sdsManagement', '_blank');
            }}
          >
            表示
          </Button>
        </Tooltip>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space size="middle">
          {isSdsCategory ? (
            <FileSearchOutlined style={{ color: '#13c2c2', fontSize: 20 }} />
          ) : (
            <FileTextOutlined style={{ color: '#1677ff', fontSize: 20 }} />
          )}
          <span>{categoryLabel} - <strong>{statusLabel}</strong></span>
          <Badge count={`${filteredData.length}件`} style={{ backgroundColor: isSdsCategory ? '#13c2c2' : '#1677ff' }} />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width='calc(100vw - 40px)'
      style={{
        padding: '20px',
        margin: '0',
        top: '20px',
        height: 'calc(100vh - 40px)',
        overflow: 'auto',
      }}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          閉じる
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16, marginTop: 8 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder={isSdsCategory ? "管理番号・商品名・申込者で検索..." : "発注番号・発注ID・商品名・取引先名で検索..."}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 340, borderRadius: 6 }}
            allowClear
          />
          <Text type="secondary">
            全 {orders.length} 件中 {filteredData.length} 件を表示
          </Text>
        </Space>
      </div>

      <Table id={statusLabel}
        dataSource={filteredData}
        columns={isSdsCategory ? sdsColumns : orderColumns}
        rowKey={(record) => record.id || record.orderNo}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => `${total}件中 ${range[0]}-${range[1]}件`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }
        }}
        bordered
        size="small"
        locale={{
          triggerAsc: 'クリックで昇順に並べ替え',
          triggerDesc: 'クリックで降順に並べ替え',
          cancelSort: 'クリックで並べ替えを解除'
        }}
        scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
      />
    </Modal>
  );
};

export default OrderListModal;
